import React, { useEffect, useRef, useState } from 'react';
import { HERO_VIDEO_URL, HERO_VIDEO_WEBM, HERO_CUES } from '../data/mock';
import { ArrowDown } from 'lucide-react';
import { ArrowUpRight } from 'lucide-react';

export default function HeroVideo() {
  const wrapRef = useRef(null);
  const videoRef = useRef(null);
  const [duration, setDuration] = useState(0);
  const [progress, setProgress] = useState(0);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [mobileIdx, setMobileIdx] = useState(0);
  const targetTimeRef = useRef(0);
  const rafRef = useRef(0);
  const autoMode = reducedMotion || isMobile;

  // Detect reduced motion + mobile
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(mq.matches);
    const onChange = () => setReducedMotion(mq.matches);
    mq.addEventListener?.('change', onChange);

    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => {
      mq.removeEventListener?.('change', onChange);
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  // Load metadata
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onMeta = () => setDuration(v.duration || 0);
    v.addEventListener('loadedmetadata', onMeta);
    if (v.readyState >= 1) onMeta();
    return () => v.removeEventListener('loadedmetadata', onMeta);
  }, []);

  // Fallback: autoplay loop on mobile / reduced motion
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (autoMode) {
      v.muted = true;
      v.loop = true;
      v.play().catch(() => {});
      const resume = () => v.play().catch(() => {});
      window.addEventListener('touchstart', resume, { once: true, passive: true });
      return () => window.removeEventListener('touchstart', resume);
    }
    v.pause();
    v.loop = false;
  }, [autoMode]);

  // Mobile: rotate messaging on a timer instead of scroll
  useEffect(() => {
    if (!autoMode) return;
    setMobileIdx(0);
    const id = setInterval(() => {
      setMobileIdx((i) => (i + 1) % HERO_CUES.length);
    }, 4500);
    return () => clearInterval(id);
  }, [autoMode]);

  // Scroll-controlled scrubbing
  useEffect(() => {
    if (autoMode) return;
    const wrap = wrapRef.current;
    const v = videoRef.current;
    if (!wrap || !v) return;

    const updateTarget = () => {
      const rect = wrap.getBoundingClientRect();
      const total = wrap.offsetHeight - window.innerHeight;
      const scrolled = Math.min(Math.max(-rect.top, 0), total);
      const p = total > 0 ? scrolled / total : 0;
      setProgress(p);
      const dur = v.duration || duration;
      if (dur > 0) targetTimeRef.current = Math.max(0, Math.min(dur - 0.05, p * dur));
    };

    const tick = () => {
      const cur = v.currentTime;
      const tgt = targetTimeRef.current;
      // Smoothly approach target time for buttery scrubbing
      const next = cur + (tgt - cur) * 0.18;
      if (Math.abs(tgt - cur) > 0.02) {
        try { v.currentTime = next; } catch (e) {}
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    updateTarget();
    rafRef.current = requestAnimationFrame(tick);
    window.addEventListener('scroll', updateTarget, { passive: true });
    window.addEventListener('resize', updateTarget);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('scroll', updateTarget);
      window.removeEventListener('resize', updateTarget);
    };
  }, [duration, autoMode]);

  // Determine active cue
  const scrollIdx = HERO_CUES.reduce((acc, c, i) => (progress >= c.at ? i : acc), 0);
  const activeIdx = autoMode ? mobileIdx : scrollIdx;
  const active = HERO_CUES[activeIdx];

  return (
    <section
      ref={wrapRef}
      id="top"
      className="hero-scroll-wrap"
      style={{ height: autoMode ? '100svh' : '320vh' }}
    >
      <div className="hero-sticky">
        <video
          ref={videoRef}
          className="hero-video"
          muted
          playsInline
          loop={autoMode}
          autoPlay={autoMode}
          preload="auto"
          aria-hidden="true"
        >
          <source src={HERO_VIDEO_WEBM} type="video/webm" />
          <source src={HERO_VIDEO_URL} type="video/mp4" />
        </video>
        <div className="hero-overlay" />
        <div className="hero-grain" />

        {/* Top edge: pill */}
        <div className="absolute top-[92px] left-0 right-0 z-10 px-6 lg:px-10 flex justify-center">
          <div className="pill">
            <span className="dot" />
            Empowerment Zone for Creatives
          </div>
        </div>

        {/* Center messaging */}
        <div className="absolute inset-0 z-10 flex items-center">
          <div className="max-w-[1400px] mx-auto w-full px-6 lg:px-10 grid lg:grid-cols-12 gap-6 lg:gap-8 items-end pb-20 lg:pb-24">
            <div className="lg:col-span-8">
              <div key={`eyebrow-${activeIdx}`} className="text-[12px] font-mono tracking-[0.2em] uppercase text-[#CFA153] mb-5">
                {active.eyebrow}
              </div>
              <h1 key={`title-${activeIdx}`} className="giant-text text-white">
                {active.title.split(' ').map((w, i) => (
                  <span key={i} className="inline-block mr-3">
                    <span
                      className="inline-block"
                      style={{
                        animation: `heroIn 700ms cubic-bezier(0.22,1,0.36,1) ${i * 55}ms both`,
                      }}
                    >
                      {w}
                    </span>
                  </span>
                ))}
              </h1>
            </div>
            <div className="lg:col-span-4 lg:pl-8">
              <p key={`body-${activeIdx}`} className="text-white/75 text-[15px] leading-[1.65] max-w-[420px]" style={{ animation: 'fadeIn 700ms ease 120ms both' }}>
                {active.body}
              </p>
              <div className="flex flex-wrap gap-3 mt-7">
                <a href="#signup" className="btn-jade">Sign Up <ArrowUpRight size={16} /></a>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom edge: progress + scroll cue */}
        <div className="absolute bottom-0 left-0 right-0 z-10">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-7 flex items-end justify-between gap-6">
            <div className="flex items-center gap-4 text-white/60 text-[11px] font-mono tracking-[0.18em] uppercase" data-testid="hero-scroll-cue">
              <ArrowDown size={14} className="text-[#22A773]" />
              {autoMode ? 'Swipe to explore' : 'Scroll to reveal'}
            </div>
            <div className="flex-1 max-w-[640px] hidden md:flex items-center gap-3">
              {HERO_CUES.map((_, i) => (
                <div key={i} className="flex-1 h-[2px] rounded-full bg-white/10 overflow-hidden">
                  <div
                    className="h-full bg-[#22A773]"
                    style={{
                      width:
                        i < activeIdx
                          ? '100%'
                          : i === activeIdx
                          ? autoMode
                            ? '100%'
                            : `${Math.min(100, Math.max(0, ((progress - HERO_CUES[i].at) / ((HERO_CUES[i + 1]?.at ?? 1) - HERO_CUES[i].at)) * 100))}%`
                          : '0%',
                      transition: 'width 220ms linear',
                    }}
                  />
                </div>
              ))}
            </div>
            <div className="flex md:hidden items-center gap-2" data-testid="hero-mobile-dots">
              {HERO_CUES.map((_, i) => (
                <span
                  key={i}
                  className="rounded-full transition-all duration-300"
                  style={{
                    width: i === activeIdx ? 18 : 6,
                    height: 6,
                    background: i === activeIdx ? '#22A773' : 'rgba(255,255,255,0.2)',
                  }}
                />
              ))}
            </div>
            <div className="text-white/60 text-[11px] font-mono tracking-[0.18em] uppercase">
              0{activeIdx + 1} / 0{HERO_CUES.length}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes heroIn {
          from { transform: translateY(110%); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px);} to { opacity: 1; transform: translateY(0);} }
      `}</style>
    </section>
  );
}
